import { useState, useEffect, useCallback } from 'react';
import { clientsAPI } from '../../api/clients';
import { formatDate, formatStatus, getStatusColor } from '../../utils/formatters';
import { Search,  Edit2, Eye,  Plus, RefreshCw, Trash2 } from 'lucide-react';
import Loader from '../common/Loader';
import Notification from '../common/Notification';
import '../../styles/admin/AdminClients.css';

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  // Suppression de la variable 'error' non utilisée
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [notification, setNotification] = useState(null);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'lead',
    projectType: '',
    projectDescription: '',
    budget: '',
    source: 'website'
  });

  const showNotification = useCallback((type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      
      const response = await clientsAPI.getAll(params);
      setClients(response.data.clients || []);
    } catch (err) {
      console.error('Erreur:', err);
      showNotification('error', 'Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, showNotification]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const handleAddNote = async (clientId) => {
    if (!newNote.trim()) return;
    
    try {
      await clientsAPI.addNote(clientId, {
        text: newNote,
        createdBy: 'Admin'
      });
      setNewNote('');
      showNotification('success', 'Note ajoutée avec succès');
      loadClients();
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors de l\'ajout de la note');
    }
  };

  const updateClientStatus = async (clientId, newStatus) => {
    try {
      await clientsAPI.update(clientId, { status: newStatus });
      showNotification('success', 'Statut mis à jour');
      loadClients();
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) return;
    
    try {
      await clientsAPI.delete(clientId);
      showNotification('success', 'Client supprimé avec succès');
      loadClients();
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors de la suppression');
    }
  };

  const handleCreateClient = async () => {
    try {
      await clientsAPI.create(clientForm);
      showNotification('success', 'Client créé avec succès');
      setShowClientForm(false);
      resetClientForm();
      loadClients();
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors de la création du client');
    }
  };

  const handleUpdateClient = async () => {
    if (!selectedClient) return;
    
    try {
      await clientsAPI.update(selectedClient._id, clientForm);
      showNotification('success', 'Client mis à jour avec succès');
      setShowClientForm(false);
      setEditMode(false);
      resetClientForm();
      loadClients();
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('error', 'Erreur lors de la mise à jour du client');
    }
  };

  const resetClientForm = () => {
    setClientForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      status: 'lead',
      projectType: '',
      projectDescription: '',
      budget: '',
      source: 'website'
    });
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setClientForm({
      name: client.name,
      email: client.email,
      phone: client.phone || '',
      company: client.company || '',
      status: client.status,
      projectType: client.projectType || '',
      projectDescription: client.projectDescription || '',
      budget: client.budget || '',
      source: client.source || 'website'
    });
    setEditMode(true);
    setShowClientForm(true);
  };

  if (loading && clients.length === 0) {
    return (
      <div className="admin-section">
        <Loader />
      </div>
    );
  }

  return (
    <div className="admin-section">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="section-header">
        <div>
          <h2>Gestion des Clients</h2>
          <p className="subtitle">{clients.length} clients trouvés</p>
        </div>
        
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={loadClients}
            disabled={loading}
          >
            <RefreshCw size={20} />
            Actualiser
          </button>
          
          <button 
            className="btn-primary"
            onClick={() => {
              resetClientForm();
              setEditMode(false);
              setShowClientForm(true);
            }}
          >
            <Plus size={20} />
            Nouveau Client
          </button>
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Rechercher client..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        
        <select 
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">Tous les statuts</option>
          <option value="lead">Lead</option>
          <option value="consultation">Consultation</option>
          <option value="quote_sent">Devis envoyé</option>
          <option value="in_progress">En cours</option>
          <option value="completed">Terminé</option>
          <option value="archived">Archivé</option>
        </select>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>{clients.length}</h3>
          <p>Clients totaux</p>
        </div>
        <div className="stat-card">
          <h3>{clients.filter(c => c.status === 'lead').length}</h3>
          <p>Leads</p>
        </div>
        <div className="stat-card">
          <h3>{clients.filter(c => c.status === 'in_progress').length}</h3>
          <p>En cours</p>
        </div>
        <div className="stat-card">
          <h3>{clients.filter(c => c.status === 'completed').length}</h3>
          <p>Terminés</p>
        </div>
      </div>

      <div className="table-container">
        <table className="clients-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Contact</th>
              <th>Projet</th>
              <th>Statut</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client._id}>
                <td>
                  <div className="client-info">
                    <div className="client-name">{client.name}</div>
                    {client.company && <div className="client-company">{client.company}</div>}
                  </div>
                </td>
                <td>
                  <div className="contact-info">
                    <div className="email">{client.email}</div>
                    {client.phone && <div className="phone">{client.phone}</div>}
                  </div>
                </td>
                <td>
                  {client.projectType && (
                    <span className="project-type">{client.projectType}</span>
                  )}
                </td>
                <td>
                  <select
                    value={client.status}
                    onChange={(e) => updateClientStatus(client._id, e.target.value)}
                    className={`status-select ${getStatusColor(client.status)}`}
                  >
                    <option value="lead">Lead</option>
                    <option value="consultation">Consultation</option>
                    <option value="quote_sent">Devis envoyé</option>
                    <option value="in_progress">En cours</option>
                    <option value="completed">Terminé</option>
                    <option value="archived">Archivé</option>
                  </select>
                </td>
                <td>{formatDate(client.createdAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      onClick={() => setSelectedClient(client)}
                      className="icon-btn"
                      title="Voir détails"
                    >
                      <Eye size={18} />
                    </button>
                    <button 
                      onClick={() => handleEditClient(client)}
                      className="icon-btn"
                      title="Modifier"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button 
                      onClick={() => handleDeleteClient(client._id)}
                      className="icon-btn danger"
                      title="Supprimer"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Client Form */}
      {showClientForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editMode ? 'Modifier le Client' : 'Nouveau Client'}</h3>
              <button onClick={() => setShowClientForm(false)} className="close-btn">×</button>
            </div>
            
            <div className="modal-content">
              <div className="form-grid">
                <div className="form-group">
                  <label>Nom complet *</label>
                  <input
                    type="text"
                    value={clientForm.name}
                    onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Téléphone</label>
                  <input
                    type="tel"
                    value={clientForm.phone}
                    onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Entreprise</label>
                  <input
                    type="text"
                    value={clientForm.company}
                    onChange={(e) => setClientForm({...clientForm, company: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Statut</label>
                  <select
                    value={clientForm.status}
                    onChange={(e) => setClientForm({...clientForm, status: e.target.value})}
                  >
                    <option value="lead">Lead</option>
                    <option value="consultation">Consultation</option>
                    <option value="quote_sent">Devis envoyé</option>
                    <option value="in_progress">En cours</option>
                    <option value="completed">Terminé</option>
                    <option value="archived">Archivé</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Type de projet</label>
                  <select
                    value={clientForm.projectType}
                    onChange={(e) => setClientForm({...clientForm, projectType: e.target.value})}
                  >
                    <option value="">Sélectionner</option>
                    <option value="website">Site Web</option>
                    <option value="ecommerce">E-commerce</option>
                    <option value="marketing">Marketing</option>
                    <option value="print">Impression</option>
                    <option value="crm">CRM</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
                
                <div className="form-group full-width">
                  <label>Description du projet</label>
                  <textarea
                    value={clientForm.projectDescription}
                    onChange={(e) => setClientForm({...clientForm, projectDescription: e.target.value})}
                    rows={3}
                  />
                </div>
                
                <div className="form-group">
                  <label>Budget (€)</label>
                  <input
                    type="number"
                    value={clientForm.budget}
                    onChange={(e) => setClientForm({...clientForm, budget: e.target.value})}
                  />
                </div>
                
                <div className="form-group">
                  <label>Source</label>
                  <select
                    value={clientForm.source}
                    onChange={(e) => setClientForm({...clientForm, source: e.target.value})}
                  >
                    <option value="website">Site web</option>
                    <option value="referral">Recommandation</option>
                    <option value="social">Réseaux sociaux</option>
                    <option value="ad">Publicité</option>
                    <option value="other">Autre</option>
                  </select>
                </div>
              </div>
              
              <div className="modal-actions">
                <button 
                  onClick={() => setShowClientForm(false)}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button 
                  onClick={editMode ? handleUpdateClient : handleCreateClient}
                  className="btn-primary"
                >
                  {editMode ? 'Mettre à jour' : 'Créer le client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Client Details */}
      {selectedClient && !showClientForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Détails Client</h3>
              <button onClick={() => setSelectedClient(null)} className="close-btn">×</button>
            </div>
            
            <div className="client-details">
              <div className="detail-group">
                <h4>Informations</h4>
                <div className="detail-grid">
                  <div>
                    <strong>Nom:</strong> {selectedClient.name}
                  </div>
                  <div>
                    <strong>Email:</strong> {selectedClient.email}
                  </div>
                  <div>
                    <strong>Téléphone:</strong> {selectedClient.phone || 'Non renseigné'}
                  </div>
                  <div>
                    <strong>Entreprise:</strong> {selectedClient.company || 'Non renseigné'}
                  </div>
                  <div>
                    <strong>Type de projet:</strong> {selectedClient.projectType || 'Non spécifié'}
                  </div>
                  <div>
                    <strong>Budget:</strong> {selectedClient.budget ? `${selectedClient.budget}€` : 'Non spécifié'}
                  </div>
                  <div>
                    <strong>Statut:</strong> <span className={`status-badge ${getStatusColor(selectedClient.status)}`}>{formatStatus(selectedClient.status)}</span>
                  </div>
                  <div>
                    <strong>Date d'ajout:</strong> {formatDate(selectedClient.createdAt)}
                  </div>
                </div>
              </div>
              
              {selectedClient.projectDescription && (
                <div className="detail-group">
                  <h4>Description du projet</h4>
                  <p>{selectedClient.projectDescription}</p>
                </div>
              )}
              
              <div className="detail-group">
                <h4>Notes</h4>
                <div className="notes-list">
                  {selectedClient.notes?.map((note, idx) => (
                    <div key={idx} className="note">
                      <div className="note-header">
                        <strong>{note.createdBy}</strong>
                        <span>{formatDate(note.createdAt)}</span>
                      </div>
                      <p>{note.text}</p>
                    </div>
                  ))}
                  {(!selectedClient.notes || selectedClient.notes.length === 0) && (
                    <p className="empty-notes">Aucune note pour ce client</p>
                  )}
                </div>
                
                <div className="add-note">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Ajouter une note..."
                    rows={3}
                  />
                  <button 
                    onClick={() => handleAddNote(selectedClient._id)}
                    className="btn-primary"
                    disabled={!newNote.trim()}
                  >
                    Ajouter la note
                  </button>
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                onClick={() => handleEditClient(selectedClient)}
                className="btn-primary"
              >
                <Edit2 size={16} />
                Modifier
              </button>
              <button 
                onClick={() => {
                  setSelectedClient(null);
                  setShowClientForm(false);
                }}
                className="btn-secondary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}